import {
	Component,
	OnInit,
	Input,
	Output,
	EventEmitter,
	ViewChild,
	ViewEncapsulation,
	forwardRef,
	SimpleChanges,
	OnChanges
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { IdService, formatNumber, formatCurrency, langTypes, FormControlBase } from '../../shared/index';

@Component({
	selector: 'rbc-slider',
	templateUrl: './slider.component.html',
	styleUrls: ['./slider.component.scss'],
	encapsulation: ViewEncapsulation.None,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: forwardRef(() => SliderComponent)
		}
	]
})
export class SliderComponent extends FormControlBase<number> implements OnInit, OnChanges {
	@Input() disabled: boolean | string = false;
	@Input() display: boolean | string = false;
	@Input() displayMinMax: boolean | string = false;
	@Input() errorLabel: string;
	@Input() follow: boolean | string = false;
	@Input() formatNumberLive: boolean | string = false;
	@Input() helpText: string;
	@Input() id: string;
	@Input() label: string;
	@Input() legendTicker: number = 0;
	@Input() max: number;
	@Input() min: number = 0;
	@Input() name: string;
	@Input() preset: number;
	@Input() step: string | number = 1;
	@Input() unit: string;
	@Input() unitPosition: string;
	@Input() lang: langTypes = 'en';

	@Output('sliderupdate') sliderUpdate = new EventEmitter();

	legends = {
		range: 0,
		interval: 0,
		middleValue: 0,
		values: []
	};
	backgroundGradient;
	widthInputRange;
	minFormatted;
	maxFormatted;
	valueFormatted;

	@ViewChild('inputSelector') $input;
	@ViewChild('outputSelector') $output;
	@ViewChild('inputOutput') $inputOutput;

	constructor(private idService: IdService) {
		super();
		this.id = idService.generate();
	}

	ngOnInit() {
		this.legends.range = this.max - this.min;
		if (this.preset) {
			if (typeof this.preset === 'string') {
				this.innerValue = parseInt(this.preset, 10);
			}
			this.innerValue = this.makeLegalValue(this.preset);
		}

		// TODO: Logic here for legend values looks iffy
		if (this.legendTicker > 0) {
			this.legends.interval = this.legends.range / this.legendTicker;
			this.legends.middleValue = this.legends.range / 2;
			const upperValues = [];
			const lowerValues = [];
			for (let i = this.legends.middleValue; i < this.max; i += this.legends.interval) {
				upperValues.push(i);
			}
			for (let i = this.legends.middleValue; i > this.min; i -= this.legends.interval) {
				if (i !== this.legends.middleValue) {
					lowerValues.push(i);
				}
			}
			lowerValues.reverse();
			this.legends.values = lowerValues.concat(upperValues);
		}

		this.minFormatted = this.$_Slider_getFormat(this.min);
		this.maxFormatted = this.$_Slider_getFormat(this.max);
		this.valueFormatted = this.getValueFormatted();
	}

	ngOnChanges(changes: SimpleChanges) {
		super.ngOnChanges(changes);
		this.$_Slider_output_FollowThumb();

		if (changes) {
			if (changes.min || changes.max) {
				this.$_Slider_backgroundGradient_update();
			}

			if (this.display && !this.follow && this.$input) {
				if (this.legendTicker > 0) {
					this.widthInputRange = this.$input.nativeElement.offsetWidth + 'px';
				}
			}
		}
	}

	ngOnDestroy(): void {
		this.idService.remove(this.id);
		super.ngOnDestroy();
	}

	// returns the legal version of the value
	makeLegalValue(value) {
		if (value > this.max) {
			return this.max;
		} else if (value < this.min) {
			return this.min;
		}
		return parseInt(value, 10);
	}

	getValueFormatted() {
		return this.$_Slider_getFormat(this.innerValue);
	}

	legendClickHandler(ticker) {
		this.$_Slider_updateValueFromLegend(ticker);
		this.$_Slider_backgroundGradient_update();
		if (this.display && !this.follow) {
			this.$inputOutput.text = this.$_Slider_formatNumberLive();
		}
	}

	// Updates from input element
	$_Slider_updateValue(inputVal: string | FocusEvent) {
		const value = this.makeLegalValue(typeof inputVal === 'string' ? inputVal : (inputVal.target as any).value);
		this.setModel(value);
		this.$_Slider_backgroundGradient_update();
	}

	onInputBlur(evt) {
		this.$_Slider_updateValue(evt);
		this.touch();
	}

	//
	$_Slider_updateValueFromLegend(ticker) {
		this.innerValue = ticker;
		this.valueFormatted = this.getValueFormatted();
	}

	$_Slider_output_FollowThumb() {
		if (this.follow) {
			const inputWidth = this.$input.nativeElement.offsetWidth - 25;
			const inputValue = this.innerValue;
			const inputMax = this.min;
			const inputMin = this.max;
			let outputPosition = (inputValue - inputMin) / (inputMax - inputMin);
			outputPosition *= inputWidth;
			const outputStyle = 'right: ' + outputPosition + 'px;';
			this.$output.nativeElement.style = outputStyle;
		}
	}

	// Update the gradient
	$_Slider_backgroundGradient_update() {
		const val = (this.innerValue - this.min) / (this.max - this.min);
		this.backgroundGradient =
			'linear-gradient(to right, #0051a5 ' + val * 100 + '%, #0051a5 ' + val * 100 + '%, #b3b3b3 ' + val * 100 + '%)';
	}

	$_Slider_formatNumberLive() {
		return this.$_Slider_getFormat(this.innerValue);
	}

	$_Slider_getFormat(number) {
		if (this.formatNumberLive) {
			number = number + '';
			const nbDecimal =
				this.lang === 'en'
					? number.split('.').length === 2
						? number.split('.')[1].length
						: 0
					: number.split(',').length === 2
					? number.split(',')[1].length
					: 0;
			if (typeof this.unit === 'undefined' || (this.display && !this.follow)) {
				return formatNumber(parseFloat(number), this.lang, nbDecimal);
			} else {
				return formatCurrency(parseFloat(number), this.lang, nbDecimal, this.unit);
			}
		} else {
			return number;
		}
	}

	setModel(val: number) {
		super.setModel(val);
		this.$_Slider_output_FollowThumb();
		this.$_Slider_backgroundGradient_update();
		this.valueFormatted = this.getValueFormatted();
		this.sliderUpdate.emit({ id: this.id, value: this.innerValue });
	}

	writeValue(val: number): void {
		this.innerValue = val;
		this.$_Slider_backgroundGradient_update();
	}
}
